import React from 'react';
import { RankExplanationCompact } from './RankExplanation';
import './TagCard.css';

/**
 * Tag Card Component
 * Displays a trending tag with rank, media count and engagement
 * 
 * @param {Object} props
 * @param {Object} props.tag - Tag data object
 * @param {Function} props.onClick - Click handler
 */
function TagCard({ tag, onClick }) {
  const {
    tag_name,
    trending_rank,
    trending_score,
    trend_count,
    platforms,
    platform_breakdown,
    rank_explanation,
    why_trending_summary,
    metrics
  } = tag;

  // Format large numbers (e.g., 1.2M, 15.3K)
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Get top platforms from breakdown
  const topPlatforms = platform_breakdown 
    ? Object.entries(platform_breakdown)
        .sort((a, b) => b[1].contribution - a[1].contribution)
        .slice(0, 3)
    : null;

  // Calculate total engagement from platform breakdown or use metrics
  const totalEngagement = metrics?.total_engagement || 
    (platform_breakdown 
      ? Object.values(platform_breakdown).reduce((sum, p) => sum + (p.total_engagement || 0), 0)
      : 0);

  return (
    <div className="tag-card" onClick={onClick}>
      {/* Header */}
      <div className="tag-card-header">
        <div className="tag-card-title">
          <span className="tag-hashtag">#</span>
          <span className="tag-name">{tag_name}</span>
        </div>
        <div className="tag-card-stats-row">
          <div className="tag-card-trend">
            <span className="trend-value">{trend_count || 0}</span>
            <span className="trend-label">media</span>
          </div>
          {totalEngagement > 0 && (
            <div className="tag-card-engagement">
              <span className="engagement-value">{formatNumber(totalEngagement)}</span>
              <span className="engagement-label">engagement</span>
            </div>
          )}
        </div>
      </div>

      {/* Rank & Score Badge */}
      {(trending_rank || trending_score) && (
        <div className="tag-card-rank-row">
          {trending_rank && (
            <span className="rank-chip">
              <span className="rank-icon">🏆</span>
              <span className="rank-value">#{trending_rank}</span>
            </span>
          )}
          {trending_score && (
            <span className="score-chip">
              <span className="score-value">{trending_score.toFixed(1)}</span>
              <span className="score-label">score</span>
            </span>
          )}
        </div>
      )}

      {/* Rank Explanation (Compact) */}
      {rank_explanation && (
        <RankExplanationCompact 
          rankExplanation={rank_explanation} 
          trendingScore={trending_score}
        />
      )}

      {/* Platform Breakdown Mini */}
      {topPlatforms && topPlatforms.length > 0 && (
        <div className="tag-card-platforms">
          {topPlatforms.map(([platform, data]) => (
            <span key={platform} className="platform-chip">
              <span className="platform-name">{platform}</span>
              <span className="platform-pct">{Math.round(data.contribution * 100)}%</span>
            </span>
          ))}
        </div>
      )}

      {/* Quick Stats (simplified) */}
      {!topPlatforms && (
        <div className="tag-card-stats">
          <div className="stat">
            <span className="stat-icon">🧭</span>
            <span className="stat-value">{platforms?.length || 0}</span>
            <span className="stat-label">platforms</span>
          </div>
        </div>
      )}

      {/* Why Trending */}
      {why_trending_summary && (
        <p className="tag-card-summary">{why_trending_summary}</p>
      )}

      {/* Action */}
      <div className="tag-card-action">
        <span className="action-text">View Details</span>
        <span className="action-arrow">→</span>
      </div>
    </div>
  );
}

export default TagCard;
